import React, { useEffect, useMemo, useState } from "react";
import Icons from "../../../Assets/Icons";
import "./specimenLogistics.css";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../utils/strings";
import { useDispatch, useSelector } from "react-redux";
import { getSpecimenLogs } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";

const FETCH_LIMIT = 1000;
const PAGE_SIZE = 10;

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const SpecimenLogisticsList = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("vn") || i18n.language?.startsWith("vi") ? "vi" : "en";
  const dispatch = useDispatch();
  const breadcrumbs = ["nursingCare", "specimenLogistics"];
  const paths = [strings.navigation.nursingCareManageService, strings.navigation.specimenLogistics];

  const { specimenLogs } = useSelector((state) => state.manageRequest);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getSpecimenLogs({ page: 1, limit: FETCH_LIMIT }));
  }, [dispatch]);

  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");

  useEffect(() => {
    setPage(1);
  }, [exportFromDate, exportToDate]);

  const filteredLogs = useMemo(() => {
    const from = exportFromDate ? new Date(exportFromDate) : null;
    const to = exportToDate ? new Date(exportToDate + "T23:59:59") : null;
    if (!from && !to) return specimenLogs;

    return specimenLogs.filter((s) => {
      const collected = s.collectionTime ? new Date(s.collectionTime) : null;
      if (!collected) return true;
      if (from && collected < from) return false;
      if (to && collected > to) return false;
      return true;
    });
  }, [specimenLogs, exportFromDate, exportToDate]);

  const totalPages = filteredLogs.length === 0 ? 0 : Math.ceil(filteredLogs.length / PAGE_SIZE);
  const currentPage = totalPages === 0 ? 1 : Math.min(page, totalPages);

  const displayLogs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE);
  }, [filteredLogs, currentPage]);

  const handleExport = () => {
    const headers = ["Patient Name", "HN Number", "Sample Name", "Homecare Staff", "Staff ID", "Collected Date Time", "Received Date Time", "Status"];
    const rows = filteredLogs.map((s) => {
      const patientName = `${s.relativeDetails?.firstName || ""} ${s.relativeDetails?.lastName || ""}`.trim();
      const hn = s.userDetails?.hnNumber || s.relativeDetails?.hnNumber || s.hnNumber || "-";
      const nurseName = s.nurseDetails ? `${s.nurseDetails.firstName?.[lang] || s.nurseDetails.firstName?.en || s.nurseDetails.firstName || ""} ${s.nurseDetails.lastName?.[lang] || s.nurseDetails.lastName?.en || s.nurseDetails.lastName || ""}`.trim() : "-";
      // const nurseId = s.nurseDetails?.employeeID || s.nurseDetails?.employeeId || "-";
      return [patientName, hn, s.sampleName || "-", nurseName, formatDateTime(s.collectionTime), formatDateTime(s.receivedDate), s.status || "-"];
    });
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `specimen_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, currentPage, { type: "ellipsis" }, totalPages);
      }
    }

    return (
      <div className="department-pagination-list">
        <button className="department-pagination-prev-list" onClick={() => handlePage(currentPage - 1)} disabled={currentPage === 1}>
          <img src={Icons.leftArrow} alt="<" />
        </button>
        {pages.map((p, i) => (
          <button
            key={i}
            onClick={() => typeof p === "number" && handlePage(p)}
            disabled={p === currentPage || p?.type === "ellipsis"}
            className={`department-pagination-btn-list ${p === currentPage ? "active-list" : ""} ${p?.type === "ellipsis" ? "ellipse" : ""}`}
          >
            {typeof p === "number" ? p : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
          </button>
        ))}
        <button className="department-pagination-next-list" onClick={() => handlePage(currentPage + 1)} disabled={currentPage === totalPages}>
          <img src={Icons.rightArrow} alt=">" />
        </button>
      </div>
    );
  };

  return (
    <div className="mr-container specimen-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="manage-service-heading specimen-heading">{t("specimenLogs")}</div>

      <div className="specimen-actions">
        <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <label style={{ fontSize: "1.3rem", color: "var(--grey-text)", fontWeight: 500 }}>{t("fromDate")}:</label>
            <input
              type="date"
              value={exportFromDate}
              onChange={(e) => setExportFromDate(e.target.value)}
              style={{ border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.5rem 0.8rem", fontSize: "1.3rem", background: "var(--main-bg)", color: "var(--text-color)" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <label style={{ fontSize: "1.3rem", color: "var(--grey-text)", fontWeight: 500 }}>{t("toDate")}:</label>
            <input
              type="date"
              value={exportToDate}
              min={exportFromDate}
              onChange={(e) => setExportToDate(e.target.value)}
              style={{ border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.5rem 0.8rem", fontSize: "1.3rem", background: "var(--main-bg)", color: "var(--text-color)" }}
            />
          </div>
        </div>
        <button className="specimen-btn specimen-export-btn" onClick={handleExport}>
          <img src={Icons.exportSpecimen} alt="" className="specimen-btn-icon" />
          <span>{t("export")}</span>
        </button>
      </div>

      <div className="specimen-list-box">
        <table className="specimen-table">
          <thead>
            <tr>
              <th>{t("patientDetails")}</th>
              <th>{t("hnNumber")}</th>
              <th>{t("sampleName")}</th>
              <th>{t("homeCareStaff")}</th>
              {/* <th>{t("nurseId")}</th> */}
              <th>{t("collectedDateTime")}</th>
              <th>{t("receivedDateTime")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {displayLogs.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "#9ca3af", padding: "2.4rem" }}>
                  {t("noRecordFound")}
                </td>
              </tr>
            ) : (
              displayLogs.map((s) => {
                const patientName = `${s.relativeDetails?.firstName || ""} ${s.relativeDetails?.lastName || ""}`.trim();
                const hn = s.userDetails?.hnNumber || s.relativeDetails?.hnNumber || s.hnNumber || "-";
                const nurseName = s.nurseDetails ? `${s.nurseDetails.firstName?.[lang] || s.nurseDetails.firstName?.en || s.nurseDetails.firstName || ""} ${s.nurseDetails.lastName?.[lang] || s.nurseDetails.lastName?.en || s.nurseDetails.lastName || ""}`.trim() : "-";
                // const nurseId = s.nurseDetails?.employeeID || s.nurseDetails?.employeeId || "-";

                return (
                  <tr key={s._id}>
                    <td>
                      <div className="patient-cell">
                        <div className="patient-info">
                          <div className="patient-name">{patientName}</div>
                        </div>
                      </div>
                    </td>
                    <td>{hn}</td>
                    <td>{s.sampleName}</td>
                    <td>{nurseName}</td>
                    {/* <td>{nurseId}</td> */}
                    <td>{formatDateTime(s.collectionTime)}</td>
                    <td>{formatDateTime(s.receivedDate)}</td>
                    <td>{s.status === "SUBMITTED" ? <span className="status-pill status-submitted">{t("submitted")}</span> : <span className="status-pill status-pending">{t("pending")}</span>}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && renderPagination()}
    </div>
  );
};

export default SpecimenLogisticsList;
